import type { SelfDescribingJson } from '@snowplow/browser-tracker';

import { AdServer, ProductType, SubProductType } from '../../types';
import {
  type AdContextDataV2,
  type BuildAdContextV2Props,
  TrackingAdPlacement,
  TrackingAdServer,
  type TrackingProductName,
  TrackingSubProductName,
} from './build-ad-context.types';

const adServerToTrackingAdServer: Record<AdServer, TrackingAdServer> = {
  FeaturedAds: 'featured-ads',
  MediaAlpha: 'media-alpha',
  AdButler: 'ad-butler',
  HealthPilot: 'health-pilot',
  // QuinStreet listings; matches the `ad_server` value Nova sends.
  NextInsure: 'next-insure',
};

const productTypeToTrackingProductName: Record<
  ProductType,
  TrackingProductName
> = {
  HEALTH: 'health',
  MEDICARE: 'medicare',
  MEDICARE_ADVANTAGE: 'medicare',
  LIFE_INSURANCE: 'life-insurance',
};

function getTrackingSubProductNameForMedicareSubProduct(
  subProductType?: SubProductType,
) {
  if (subProductType === SubProductType.Enum.MEDICARE_SUPPLEMENT) {
    return TrackingSubProductName.Enum['medicare-supplement'];
  }
  if (subProductType === SubProductType.Enum.MEDICARE_ADVANTAGE) {
    return TrackingSubProductName.Enum['medicare-advantage'];
  }
  return TrackingSubProductName.Enum['medicare-generic'];
}

function getTrackingSubProductNameFromProductAndSubProduct(
  productType: ProductType,
  subProductType?: SubProductType,
) {
  if (productType === ProductType.Enum.MEDICARE_ADVANTAGE) {
    return TrackingSubProductName.Enum['medicare-advantage'];
  }

  if (productType === ProductType.Enum.MEDICARE) {
    return getTrackingSubProductNameForMedicareSubProduct(subProductType);
  }

  // Default to ACA for productType HEALTH
  return TrackingSubProductName.Enum.aca;
}

function getTrackingPlacementFromPlacement() {
  // Currently, we only have one placement type "TYP"
  return TrackingAdPlacement.Enum.typ;
}

export function buildAdContextV2({
  adServer,
  advertiser,
  publisher,
  impressionPosition,
  searchMethod,
  productFit,
  groupId,
}: BuildAdContextV2Props): SelfDescribingJson<AdContextDataV2> {
  const {
    campaignName,
    creativeId,
    creativeTitle,
    name: advertiserName,
  } = advertiser;

  const { adId, productType, subProductType } = publisher;

  const trackingAdServer = adServerToTrackingAdServer[adServer];
  const publisherProductName = productTypeToTrackingProductName[productType];
  const publisherSubProductName =
    getTrackingSubProductNameFromProductAndSubProduct(
      productType,
      subProductType,
    );
  const trackingPlacement = getTrackingPlacementFromPlacement();
  const trackingGroupId = groupId ? `${groupId}` : undefined;

  const data: AdContextDataV2 = {
    ad_server: trackingAdServer,
    advertiser_creative_id: `${creativeId}`,
    advertiser_creative_title: creativeTitle,
    advertiser_name: advertiserName,
    advertiser_campaign_name: campaignName,
    publisher_ad_id: adId,
    publisher_product_name: publisherProductName,
    publisher_sub_product_name: publisherSubProductName,
    publisher_placement: trackingPlacement,
    impression_position: impressionPosition,
    search_method: searchMethod,
    product_fit: productFit,
    group_id: trackingGroupId,
  };

  return {
    schema: 'iglu:com.healthcare/ad/jsonschema/2-0-3',
    data,
  };
}
